import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import OrganizationDetails from "./OrganizationDetails";
import {
  OrganizationResponseDto,
  OrganizationCreateDto,
} from "../common/ObjectTypes";
import {
  createOrganization,
  getCountries,
  getOrganizationByGuid,
  getOrganizationPermissions,
  getOrganizations,
  getParentOrganizationOptions,
  updateOrganization,
} from "../../api/organizationsApi";
import { toast } from "react-toastify";
import { BeatLoader } from "react-spinners";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { FIELD_LIMITS, lengthError } from "../common/fieldLimits";
import ManageOrganizationUsers from "./ManageOrganizationUsers";
import OrganizationProjectsTab from "./OrganizationProjectsTab";
import OrganizationSensorsTab from "./OrganizationSensorsTab";
import OrganizationDetailsOverview from "./OrganizationDetailsOverview";
import SideMenu from "../common/SideMenu";
import NoAccess from "../common/NoAccess";
import tailwindClassnames from "../common/TailwindClassnames";
import { isForbiddenError } from "../../api/apiUtils";
import { useProfile } from "../common/ProfileProvider";
import { OrganizationRoles } from "../common/Enums";

type ManageOrganizationDetailsProps = {
  existingOrganization: boolean;
};

const ManageOrganizationDetails: React.FC<ManageOrganizationDetailsProps> = ({
  existingOrganization,
}) => {
  const { guid } = useParams();
  const { subpage } = useParams();
  const navigate = useNavigate();
  const [org_id, setOrgId] = useState<number | undefined>();
  const [perms, setPerms] = useState([]);
  const [permsLoaded, setPermsLoaded] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [countries, setCountries] = useState<string[]>([]);
  const emptyOrg = {
    org_name: "",
    parent_organization_id: undefined,
    address: null,
    country_id: "",
    comments: null,
    website: null,
    primary_contact_email: "",
    primary_contact_phone: null,
    inherit_permissions: true,
  };
  const [organization, setOrganization] =
    useState<OrganizationCreateDto>(emptyOrg);
  const [originalOrganization, setOriginalOrganization] =
    useState<OrganizationResponseDto | null>(null);
  const [parentOrganizationOptions, setParentOrganizationOptions] = useState<
    OrganizationResponseDto[]
  >([]);
  const { t } = useTranslation();
  const { profile } = useProfile();
  // Anyone may create an organization, but only under one they own: the API refuses any
  // other parent (Akili-Wiki#595). Admins may use any. Filtered here, not when the list
  // loads, so a profile that arrives after the list still applies, without a refetch.
  const offeredParentOptions = React.useMemo(
    () =>
      existingOrganization || profile?.is_admin
        ? parentOrganizationOptions
        : parentOrganizationOptions.filter((org) =>
            profile?.org_roles.some(
              (role) =>
                role.org_id === org.org_id &&
                role.role === OrganizationRoles.OWNER,
            ),
          ),
    [existingOrganization, parentOrganizationOptions, profile],
  );

  // Load organization and permissions when guid changes.
  useEffect(() => {
    // Set by the cleanup when `guid` changes under an in-flight request, so a refusal for the
    // organization the route has already left cannot land on the one it shows now (Copilot
    // review, cycle 2). Scoped to the 403 branch this ticket added; the success path's
    // setters have the same exposure and always have — a page-wide guard is its own change.
    let cancelled = false;
    const loadDataAndPerms = async () => {
      // The route component is reused when only `:guid` changes, so a refusal for one
      // organization must not stick to the next one (Copilot review, cycle 1).
      setForbidden(false);
      try {
        if (guid && existingOrganization) {
          // The primary fetch has its own catch, so only ITS 403 maps to the page-level
          // no-access state — a refusal from the permissions lookup below is not "you may
          // not see this organization" (Copilot review, cycle 4). `getOrganizationByGuid`
          // rethrows since AKL-939; before that it resolved `undefined` on any failure and
          // the not-found branch below handled it, so every other rejection takes that same
          // path here rather than leaving the page on its spinner.
          let tempOrganization: OrganizationResponseDto | undefined;
          try {
            tempOrganization = await getOrganizationByGuid(guid);
          } catch (err) {
            if (cancelled) return;
            if (isForbiddenError(err)) {
              // The API refuses `GET /organizations/guid/{guid}` with a 403 to a user
              // without VIEW_ORG_DETAILS — a deep link or a stale bookmark. Show the shared
              // no-access state instead of an empty form (AKL-939). `permsLoaded` never
              // becomes true on this path, so the effect that normally ends the spinner
              // never runs; end it here.
              setForbidden(true);
              setIsLoading(false);
              return;
            }
            console.error("Error loading organization:", err);
            tempOrganization = undefined;
          }
          if (!tempOrganization) {
            toast.error(t("Organization not found"));
            navigate("/organizations");
            return;
          }
          setOrgId(tempOrganization.org_id);
          setOrganization(tempOrganization);
          setOriginalOrganization(tempOrganization);
          // The permissions lookup is secondary: whatever happens to it, the organization
          // already loaded stays on screen and loading completes — `permsLoaded` is what
          // starts the effect that ends the spinner (Copilot review, cycle 5). Its wrapper
          // rethrows, so before this a refused or failed lookup left the page on the spinner.
          try {
            const temp = await getOrganizationPermissions(
              Number(tempOrganization?.org_id),
            );
            setPerms(temp ?? []);
          } catch (err) {
            console.error("Error loading organization permissions:", err);
            setPerms([]);
          }
        }
        setPermsLoaded(true);
      } catch (err) {
        console.error("Error loading organization or permissions:", err);
      }
    };

    loadDataAndPerms();
    return () => {
      cancelled = true;
    };
  }, [guid, existingOrganization]);

  // Load parent organizations when permissions are loaded.
  useEffect(() => {
    const loadParentOrganizations = async () => {
      try {
        if (org_id && existingOrganization) {
          const temp = await getParentOrganizationOptions(Number(org_id));
          setParentOrganizationOptions(temp);
        } else {
          const temp = await getOrganizations({
            all_orgs: true,
            skip: 0,
          });
          setParentOrganizationOptions(temp.organizations);
        }
      } catch (error) {
        console.error("Error loading parent organizations:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (permsLoaded) {
      loadParentOrganizations();
    }
  }, [permsLoaded, org_id]);

  // Load countries when language changes
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const temp = await getCountries(i18n.language);
        setCountries(temp);
      } catch (error) {
        console.error(t("Error loading countries:"), error);
      }
    };
    if (permsLoaded) {
      loadCountries();
    }
  }, [t, permsLoaded]);

  // Clear errors when required fields in form data change, if form has been saved at least once.
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      formIsValid();
    }
  }, [
    organization.org_name,
    organization.primary_contact_email,
    organization.country_id,
  ]);

  const handleSelectParentOrg = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setOrganization((prevState) => ({
        ...prevState,
        parent_organization_id: Number(selectedOption.value),
      }));
    } else {
      setOrganization((prevState) => ({
        ...prevState,
        parent_organization_id: undefined,
      }));
    }
  };

  const handleSelectCountry = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setOrganization((prevState) => ({
        ...prevState,
        country_id: selectedOption.value,
      }));
    }
    if (!selectedOption) {
      setOrganization((prevState) => ({
        ...prevState,
        country_id: "",
      }));
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { type, name, value, checked } = e.target as HTMLInputElement;

    // Check the type of the input to determine how to handle the value
    let inputValue: any = type === "checkbox" ? checked : value;
    if (inputValue === "") {
      inputValue = null;
    }
    // Update state accordingly
    setOrganization((prevState) => ({
      ...prevState,
      [name]: inputValue,
    }));
  };

  const handleCancel = () => {
    navigate("/organizations");
  };

  const formIsValid = (): boolean => {
    const newErrors: any = {};

    if (!organization.org_name) {
      newErrors.org_name = t("Organization name is required");
    }
    if (!organization.primary_contact_email) {
      newErrors.primary_contact_email = t("Email is required");
    }
    if (!organization.country_id) {
      newErrors.country_id = t("Country is required");
    }

    // AKL-728: one shared message for every field, with the limit interpolated rather
    // than written into the copy. The old messages hardcoded a number and drifted -- the
    // email one said 255 while the check was 50, matching neither the column (also 50)
    // nor Wildlife Insights (255). Limits come from common/fieldLimits.ts, which mirrors
    // the API's column widths.
    const limited: [keyof typeof organization, string, number][] = [
      ["org_name", t("Organization Name"), FIELD_LIMITS.TEXT],
      ["primary_contact_email", t("Primary Contact Email"), FIELD_LIMITS.EMAIL],
      [
        "primary_contact_phone",
        t("Primary Contact Phone Number"),
        FIELD_LIMITS.PHONE,
      ],
      ["address", t("Address"), FIELD_LIMITS.ADDRESS],
      ["website", t("Website"), FIELD_LIMITS.TEXT],
      ["comments", t("Comments"), FIELD_LIMITS.NARRATIVE],
    ];
    limited.forEach(([field, label, limit]) => {
      const message = lengthError(
        t,
        label,
        organization[field] as string | null | undefined,
        limit,
        i18n.language,
      );
      if (message) newErrors[field] = message;
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let canSave = formIsValid();
    if (!canSave) {
      return;
    } else if (!existingOrganization) {
      try {
        setIsSaving(true);
        await createOrganization(organization);
        toast.success(t("Organization created successfully!"));
        navigate("/organizations");
        // Redirect the user or perform any other action
      } catch (error: any) {
        toast.error(
          t("Failed to create organization:") + error.data.detail.toString(),
        );
      } finally {
        setIsSaving(false);
      }
    } else {
      try {
        setIsSaving(true);
        const updatedOrg = await updateOrganization(
          Number(org_id),
          organization,
        );
        setOriginalOrganization(updatedOrg);
        setOrganization(updatedOrg);
        toast.success(t("Organization updated successfully!"));
        // Redirect the user or perform any other action
      } catch (error) {
        console.error("Error updating organization:", error);
        toast.error(t("Failed to update organization"));
      } finally {
        setIsSaving(false);
      }
    }
  };

  function handleSelectSubPage(e: string) {
    navigate("/organizations/" + guid + "/" + e);
  }

  return isLoading ? (
    <div className="sweet-loading flex justify-center items-center mt-8">
      <BeatLoader
        color={tailwindClassnames.skyBlueDark}
        loading={true}
        size={15}
        margin={5}
      />
    </div>
  ) : forbidden ? (
    <div className="flex flex-row w-full">
      <div>
        <SideMenu />
      </div>
      <div className="flex flex-col w-full mx-8">
        <NoAccess />
      </div>
    </div>
  ) : (
    <div className="flex flex-row w-full">
      <div>
        <SideMenu />
      </div>
      <div className="flex flex-col w-full mx-8">
        <OrganizationDetails
          orgName={
            originalOrganization?.org_name ? originalOrganization?.org_name : ""
          }
          perms={perms}
          existingOrganization={existingOrganization}
          subPage={subpage}
          onSelectSubPage={handleSelectSubPage}
        />
        {(subpage === "overview" || !existingOrganization) && (
          <OrganizationDetailsOverview
            organization={organization}
            parentOrganizationOptions={offeredParentOptions}
            countries={countries}
            existingOrganization={existingOrganization}
            perms={perms}
            errors={errors}
            isSaving={isSaving}
            onSelectParentOrg={handleSelectParentOrg}
            onSelectCountry={handleSelectCountry}
            onChange={handleChange}
            onCancel={handleCancel}
            onSave={handleSubmit}
            saveDisabled={
              JSON.stringify(originalOrganization) ===
              JSON.stringify(organization)
            }
          />
        )}
        {subpage === "users" && (
          <ManageOrganizationUsers
            orgId={Number(org_id)}
            orgName={
              originalOrganization?.org_name
                ? originalOrganization?.org_name
                : ""
            }
            perms={perms}
          />
        )}
        {subpage === "projects" && (
          <OrganizationProjectsTab
            orgId={Number(org_id)}
            orgGuid={guid || ""}
            perms={perms}
          />
        )}
        {subpage === "sensors" && (
          <OrganizationSensorsTab
            orgId={Number(org_id)}
            orgGuid={guid || ""}
            perms={perms}
          />
        )}
      </div>
    </div>
  );
};

export default ManageOrganizationDetails;
